import {inject, Injectable, signal} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {BillingStatus, OrganizationEntity} from '../../models/organizations';
import {User} from '../../models/user';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private url = environment.backendUrl;
  public organizations = signal<OrganizationEntity[]>([]);
  public currentOrganization = signal<OrganizationEntity | null>(null);
  public lastBillingStatus = signal<BillingStatus | null>(null);
  currentOrganizationId: string = localStorage.getItem('impersonatingOrgId') ?? '01JDBB3NV8RCPJFQ11W6FXER1N';
  isImpersonating = !!localStorage.getItem('impersonatingOrgId');

  // User impersonation state
  isImpersonatingUser = !!localStorage.getItem('impersonatingUserId');
  impersonatingUserId: string | null = localStorage.getItem('impersonatingUserId');
  impersonatingUserName: string | null = localStorage.getItem('impersonatingUserName');

  http = inject(HttpClient);

  setIsImpersonating(orgId: string | null) {
    if (orgId == null) {
      this.isImpersonating = false;
      localStorage.removeItem('impersonatingOrgId');
    } else {
      this.isImpersonating = true;
      localStorage.setItem('impersonatingOrgId', orgId);
      // Clear user impersonation to avoid conflicts
      localStorage.removeItem('impersonatingUserId');
      localStorage.removeItem('impersonatingUserName');
      localStorage.removeItem('impersonatingUserOrgId');
    }
    location.reload();
  }

  startImpersonatingUser(user: User) {
    const orgId = user.organizations?.[0]?.id;
    if (!orgId) return;
    localStorage.setItem('impersonatingUserId', user.id);
    localStorage.setItem('impersonatingUserName', user.name);
    localStorage.setItem('impersonatingUserOrgId', orgId);
    // Clear org impersonation to avoid conflicts
    localStorage.removeItem('impersonatingOrgId');
    location.reload();
  }

  stopImpersonatingUser() {
    localStorage.removeItem('impersonatingUserId');
    localStorage.removeItem('impersonatingUserName');
    localStorage.removeItem('impersonatingUserOrgId');
    location.reload();
  }

  async setCurrentOrganizationId(id: string) {
    // User impersonation takes priority
    const impersonatingUserOrgId = localStorage.getItem('impersonatingUserOrgId');
    if (impersonatingUserOrgId != null) {
      this.currentOrganizationId = impersonatingUserOrgId;
      await this.getCurrentOrganization();
      this.isImpersonatingUser = true;
      return;
    }
    const impersonatingOrgId = localStorage.getItem('impersonatingOrgId');
    if (impersonatingOrgId != null) {
      this.currentOrganizationId = impersonatingOrgId;
      await this.getCurrentOrganization();
      this.isImpersonating = true;
      return;
    }
    this.currentOrganizationId = id;
    await this.getCurrentOrganization();
  }

  async getAllOrganizations() {
    const organizations = await lastValueFrom(this.http.get<OrganizationEntity[]>(this.url+`/organizations`));
    this.organizations.set(organizations);
    return organizations;
  }

  async updateBillingStatus(orgId: string, billingStatus: BillingStatus) {
    await lastValueFrom(this.http.patch(this.url+`/organizations/${orgId}`, {billingStatus}));
    this.organizations.update(orgs => orgs.map(o => o.id === orgId ? {...o, billingStatus} : o));
    if (this.currentOrganization()?.id === orgId) {
      this.currentOrganization.update(o => o ? {...o, billingStatus} : o);
    }
  }

  async getCurrentOrganization() {
    const org = await lastValueFrom(this.http.get<OrganizationEntity>(this.url+`/organizations/${this.currentOrganizationId}`));
    this.currentOrganization.set(org);
    return org;
  }

}
